/**
 * Mobile cart bar for the family grid — the bottom-pinned summary the right
 * rail can't show on small screens. Cart icon + subtotal + item count on the
 * left, "Review & Checkout" on the right. Hidden on desktop. Mirrors the school
 * `MobileCartBar`.
 */

import { Button } from '@ordo/ui/button'
import { ShoppingCart } from 'lucide-react'
import type { ReactElement } from 'react'

import { CartTotalPrice } from '@/components/orders/family/cart-total-price'
import { useCartPricePreview } from '@/components/orders/family/use-cart-price-preview'
import { subtotalCents, totalQty } from '@/lib/family-orders/cart'
import { useAllCartLines } from '@/stores/family-cart'

export interface FamilyMobileCartBarProps {
  onCheckout: () => void
  submitting?: boolean
}

export function FamilyMobileCartBar({ onCheckout, submitting }: FamilyMobileCartBarProps): ReactElement {
  // Every campus's lines — the family cart aggregates all children.
  const lines = useAllCartLines()
  const qty = totalQty(lines)
  // What the family will actually pay (net of subsidies), from the
  // shared server preview; gross subtotal only while it loads.
  const preview = useCartPricePreview()
  const currency = lines.find((l) => l.currency)?.currency ?? 'USD'
  return (
    <div className="-mx-4 flex shrink-0 items-center justify-between gap-3 border-t bg-background px-4 py-3 lg:hidden">
      <div className="flex items-center gap-3">
        <ShoppingCart className="h-6 w-6 shrink-0 text-foreground" strokeWidth={1.75} />
        <div className="flex flex-col leading-tight">
          <CartTotalPrice
            totals={preview.totals}
            fallbackCents={subtotalCents(lines)}
            currency={currency}
            className="text-base font-semibold text-foreground"
          />
          <span className="text-xs text-muted-foreground">
            {qty} {qty === 1 ? 'item' : 'items'}
          </span>
        </div>
      </div>
      <Button onClick={onCheckout} disabled={submitting || qty === 0}>
        {submitting ? 'Submitting…' : 'Review & Checkout'}
      </Button>
    </div>
  )
}
